import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Globe2, Compass, ShieldCheck, ArrowRight } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { CText } from "../components/CText";

const HERO = "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920";

const VALUE_ICONS = [ShieldCheck, Compass, Globe2, Award];

export default function About() {
  const { get } = useContent();

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end pb-16 pt-32">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F16] via-[#0A0F16]/70 to-[#0A0F16]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <CText section="about" field="hero_overline" as="div" className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059] mb-4" />
            <h1 className="font-serif-display text-5xl md:text-7xl text-white leading-[1.05] max-w-4xl tracking-tight font-light">
              <CText section="about" field="hero_line1" as="span" className="block" />
              <CText section="about" field="hero_line2" as="span" className="block text-[#C5A059] italic font-thin" />
            </h1>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid md:grid-cols-[200px_1fr] gap-12">
          <div>
            <CText section="about" field="manifesto_overline" as="div" className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059]" />
            <div className="mt-3 w-12 h-[1px] bg-[#C5A059]/40" />
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1 }}>
            <CText section="about" field="manifesto" as="p" preserveNewlines
              className="font-sans-body text-base md:text-lg text-[#A3A9B2] leading-[1.85]"
            />
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 border-t border-[#C5A059]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <CText section="about" field="values_overline" as="div" className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059] mb-3" />
          <CText section="about" field="values_title" as="h2" className="font-serif-display font-light text-3xl md:text-5xl text-white max-w-2xl mb-16 leading-tight" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1,2,3,4].map((i) => {
              const Icon = VALUE_ICONS[i-1];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: (i-1) * 0.1 }}
                  className="border border-[#C5A059]/15 bg-[#111822] p-8 hover:border-[#C5A059]/40 transition-colors"
                  data-testid={`value-${i}`}
                >
                  <Icon size={28} className="text-[#C5A059] mb-6" />
                  <CText section="about" field={`value${i}_title`} as="h3" className="font-sans-body font-semibold text-xl text-white mb-3 tracking-tight" />
                  <CText section="about" field={`value${i}_desc`} as="p" className="text-sm text-[#A3A9B2] leading-relaxed" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-24 border-t border-[#C5A059]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6">
            {[1,2,3,4].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.8, delay: (i-1) * 0.1 }}
                className="text-center md:text-left"
                data-testid={`stat-${i}`}
              >
                <CText section="about" field={`stat${i}_value`} as="div" className="font-serif-display font-extralight text-5xl md:text-6xl text-[#C5A059] mb-2 tracking-tight" />
                <CText section="about" field={`stat${i}_label`} as="div" className="text-xs tracking-[0.2em] uppercase text-[#A3A9B2]" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 border-t border-[#C5A059]/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif-display font-light text-3xl md:text-5xl text-white leading-tight max-w-3xl mx-auto mb-10">
            <CText section="about" field="cta_title_a" as="span" className="block" />
            <CText section="about" field="cta_title_b" as="span" className="text-[#C5A059] italic font-thin" />
          </h2>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-[#C5A059] text-[#0A0F16] px-10 py-4 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C07B] transition-colors group"
            data-testid="about-contact-cta"
          >
            <CText section="about" field="cta_button" as="span" /> <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
